import { ReactElement, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';

import { Box, Chip, List, ListItem, SxProps, Theme, Typography } from '@mui/material';

import { ROIHorizonModalContent } from './ROIHorizonModalContent';
import { ROIChartStrings, ROILevel, ROILevelThresholds } from './roiMethodConfig';
import { ROIModalCard } from './ROIModalCard';
import { InfoModal } from '../InfoModal';

const getChipIcon = ({
  total_roi,
  strings,
  levelThresholds,
  onClick,
}: {
  total_roi: number;
  strings: ROIChartStrings;
  levelThresholds: ROILevelThresholds;
  onClick: () => void;
}): ReactElement => {
  const chipType = ((): ROILevel => {
    if (total_roi >= levelThresholds.veryGoodROI) return 'veryGoodROI';
    if (total_roi >= levelThresholds.goodROI) return 'goodROI';

    return 'lowROI';
  })();

  return (
    <Chip
      id="roi-level-chip"
      variant="badge"
      size="md"
      type="leading"
      label={strings.modalCards[chipType].chartChipLabel}
      icon={
        <AppIcon
          iconName={chipType === 'lowROI' ? 'ThumbsDown' : 'ThumbsUp'}
          diameter="20px"
          color={strings.modalCards[chipType].chipColor}
        />
      }
      sx={chartChipSx}
      onClick={onClick}
    />
  );
};

type ROILineChartHeaderProps = Readonly<{
  total_roi: number;
  strings: ROIChartStrings;
  levelThresholds: ROILevelThresholds;
}>;

export const ROILineChartHeader = ({
  total_roi,
  strings,
  levelThresholds,
}: ROILineChartHeaderProps) => {
  const [isROIHorizonModalOpen, setIsROIHorizonModalOpen] = useState(false);
  const [isROILevelsModalOpen, setIsROILevelsModalOpen] = useState(false);

  return (
    <>
      {/** ROI HORIZON MODAL */}
      <InfoModal
        open={isROIHorizonModalOpen}
        onClose={() => setIsROIHorizonModalOpen(false)}
        headerContent={
          <Typography variant="text-lg-semibold">{strings.roiHorizonModal.title}</Typography>
        }
        bodyContent={<ROIHorizonModalContent strings={strings.roiHorizonModal} />}
      />

      {/** ROI LEVELS MODAL */}
      <InfoModal
        open={isROILevelsModalOpen}
        onClose={() => setIsROILevelsModalOpen(false)}
        headerContent={
          <Box sx={headerContentContainerSx}>
            <Typography
              variant="text-lg-semibold"
              color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
            >
              {strings.explanationTitle}
            </Typography>

            {/* What it is */}
            <Box sx={sectionSx}>
              <Typography variant="text-sm-bold">{strings.whatItIs.title}</Typography>
              {strings.whatItIs.paragraphs.map((paragraph, index) => (
                <Typography key={index} variant="text-sm-regular" sx={paragraphSx}>
                  {paragraph}
                </Typography>
              ))}
            </Box>

            {/* Why this matters */}
            <Box sx={sectionSx}>
              <Typography variant="text-sm-bold">{strings.whyItMatters.title}</Typography>
              {strings.whyItMatters.paragraphs.map((paragraph, index) => (
                <Typography key={index} variant="text-sm-regular" sx={paragraphSx}>
                  {paragraph}
                </Typography>
              ))}
              <List sx={bulletListSx}>
                {strings.whyItMatters.bulletPoints.map((point, index) => (
                  <ListItem key={index} sx={bulletItemSx}>
                    <Typography variant="text-sm-regular">{point}</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* How to interpret */}
            <Typography variant="text-sm-bold">{strings.howToInterpret.title}</Typography>
          </Box>
        }
        bodyContent={
          <Box sx={bodyContentSx}>
            {Object.values(strings.modalCards).map((card) => (
              <ROIModalCard
                key={card.title}
                title={card.title}
                chipLabelPrefix={card.chipLabelPrefix}
                chipLabelThreshold={card.chipLabelThreshold}
                chipColor={card.chipColor}
                iconName={card.iconName}
                cardDescriptions={card.cardDescription}
              />
            ))}
          </Box>
        }
      />

      {/** CHART HEADER CONTENT */}
      <Box sx={titleContentSx}>
        <Box sx={titleSx}>
          <Typography variant="text-lg-semibold" component="div">
            {strings.title}
          </Typography>

          {/* Top-left question mark - opens ROI Horizon modal */}
          <Box
            id="roi-horizon-help-btn"
            sx={tooltipIconSx}
            onClick={() => setIsROIHorizonModalOpen(true)}
          >
            <AppIcon iconName="HelpCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
          </Box>
        </Box>
        <Box sx={totalInvestmentSx}>
          <Typography variant="display-sm-semibold" component="div">
            {formatCurrency(total_roi)}
          </Typography>

          {getChipIcon({
            total_roi,
            strings,
            levelThresholds,
            onClick: () => setIsROILevelsModalOpen(true),
          })}
        </Box>
      </Box>
    </>
  );
};

const titleContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const totalInvestmentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  mb: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: { xs: 'flex-start', sm: 'center' },
  flexDirection: { xs: 'row', sm: 'row' },
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-sm'],
});

const tooltipIconSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const chartChipSx: SxProps<Theme> = ({ typography, backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  cursor: 'pointer',
  '& .MuiChip-label': {
    ...typography['text-lg-medium'],
  },
});

const bodyContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const headerContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const paragraphSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const bulletListSx: SxProps<Theme> = ({ backrs }) => ({
  margin: 0,
  paddingLeft: backrs.spacing['spacing-2xl'],
  listStyleType: 'disc',
});

const bulletItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'list-item',
  padding: 0,
  paddingLeft: backrs.spacing['spacing-xs'],
  color: backrs.colors['Colors/Text/text-secondary'],
});
